// Reusable "where do you want jobs from?" control for job alerts.
// Used by ProfileSettings (account default) and JobAlertsPanel (per alert).
import React, { useEffect, useState } from 'react';
import { MapPin, X, CheckCircle2, AlertTriangle, Loader2, Plus } from 'lucide-react';
import type { Lang } from '@/data/constants';
import {
  GeoSettings, RADIUS_OPTIONS, RadiusMiles, MAX_SERVICE_CITIES,
  normalizeZip, parseCities, isKnownZip,
} from '@/lib/geoAlerts';

interface Props {
  value: GeoSettings;
  onChange: (v: GeoSettings) => void;
  lang: Lang;
  /** Cities from the profile's free-text "service areas", offered as a one-click import. */
  suggestedCities?: string[];
  idPrefix?: string;
}

const inputCls = 'w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-slate-900 focus:outline-none';

const ServiceAreaFields: React.FC<Props> = ({ value, onChange, lang, suggestedCities = [], idPrefix = 'geo' }) => {
  const es = lang === 'es';
  const [zipText, setZipText] = useState(value.zip || '');
  const [zipState, setZipState] = useState<'idle' | 'checking' | 'ok' | 'unknown'>('idle');
  const [cityText, setCityText] = useState('');

  useEffect(() => { setZipText(value.zip || ''); }, [value.zip]);

  // Verify the ZIP against the reference table (debounced).
  useEffect(() => {
    const z = normalizeZip(zipText);
    if (!z) { setZipState('idle'); return; }
    setZipState('checking');
    let cancelled = false;
    const t = window.setTimeout(() => {
      isKnownZip(z).then((ok) => { if (!cancelled) setZipState(ok ? 'ok' : 'unknown'); });
    }, 300);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [zipText]);

  const setZip = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 5);
    setZipText(digits);
    onChange({ ...value, zip: normalizeZip(digits) });
  };
  const setRadius = (r: RadiusMiles) => onChange({ ...value, radius_miles: r });
  const addCities = (raw: string | string[]) => {
    onChange({ ...value, service_cities: parseCities([...value.service_cities, ...(Array.isArray(raw) ? raw : parseCities(raw))]) });
    setCityText('');
  };
  const removeCity = (c: string) => onChange({ ...value, service_cities: value.service_cities.filter((x) => x !== c) });

  const importable = suggestedCities.filter((c) => !value.service_cities.some((x) => x.toLowerCase() === c.toLowerCase()));
  const radiusNeedsZip = value.radius_miles != null && !value.zip;

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div>
        <label htmlFor={`${idPrefix}-zip`} className="mb-1.5 block text-sm font-bold text-slate-800">
          {es ? 'Código postal de su base' : 'Your base ZIP code'}
        </label>
        <div className="relative">
          <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            id={`${idPrefix}-zip`}
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={5}
            value={zipText}
            onChange={(e) => setZip(e.target.value)}
            placeholder="63101"
            className={`${inputCls} pl-9`}
          />
        </div>
        <p className="mt-1.5 flex items-center gap-1.5 text-xs">
          {zipState === 'checking' && <><Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" /><span className="text-slate-500">{es ? 'Verificando…' : 'Checking…'}</span></>}
          {zipState === 'ok' && <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /><span className="text-emerald-700">{es ? 'Código postal reconocido' : 'ZIP recognised'}</span></>}
          {zipState === 'unknown' && <><AlertTriangle className="h-3.5 w-3.5 text-amber-600" /><span className="text-amber-700">{es ? 'No reconocido aún: usaremos todo el estado hasta verificarlo.' : 'Not recognised yet — we will use statewide matching until it is verified.'}</span></>}
          {zipState === 'idle' && <span className="text-slate-500">{es ? 'Solo se usa para medir distancias. No se muestra a nadie.' : 'Only used to measure distance. Never shown to anyone.'}</span>}
        </p>
      </div>

      <div>
        <label htmlFor={`${idPrefix}-radius`} className="mb-1.5 block text-sm font-bold text-slate-800">
          {es ? 'Distancia máxima' : 'How far will you travel?'}
        </label>
        <select
          id={`${idPrefix}-radius`}
          value={value.radius_miles == null ? '' : String(value.radius_miles)}
          onChange={(e) => setRadius(e.target.value ? (Number(e.target.value) as RadiusMiles) : null)}
          className={inputCls}
        >
          {RADIUS_OPTIONS.map((o) => (
            <option key={String(o.value)} value={o.value == null ? '' : String(o.value)}>{es ? o.es : o.en}</option>
          ))}
        </select>
        <p className={`mt-1.5 text-xs ${radiusNeedsZip ? 'font-semibold text-amber-700' : 'text-slate-500'}`}>
          {radiusNeedsZip
            ? es ? 'Ingrese su código postal para activar la distancia.' : 'Enter your ZIP to turn the radius on.'
            : es ? 'Con distancia también recibe proyectos cercanos en estados vecinos.' : 'A radius also includes nearby projects across state lines.'}
        </p>
      </div>

      <div className="sm:col-span-2">
        <label htmlFor={`${idPrefix}-cities`} className="mb-1.5 block text-sm font-bold text-slate-800">
          {es ? 'Ciudades de servicio (opcional)' : 'Service-area cities (optional)'}
        </label>
        <div className="flex gap-2">
          <input
            id={`${idPrefix}-cities`}
            value={cityText}
            onChange={(e) => setCityText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (cityText.trim()) addCities(cityText); } }}
            placeholder={es ? 'Ej. Fontana, Riverside' : 'e.g. Fontana, Riverside'}
            className={inputCls}
            disabled={value.service_cities.length >= MAX_SERVICE_CITIES}
          />
          <button
            type="button"
            onClick={() => cityText.trim() && addCities(cityText)}
            className="inline-flex shrink-0 items-center gap-1 rounded-xl border border-slate-300 px-3 text-sm font-bold text-slate-700 hover:border-slate-400"
          >
            <Plus className="h-4 w-4" /> {es ? 'Agregar' : 'Add'}
          </button>
        </div>
        {value.service_cities.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {value.service_cities.map((c) => (
              <span key={c} className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-3 py-1 text-xs font-bold text-orange-700">
                {c}
                <button type="button" onClick={() => removeCity(c)} aria-label={`${es ? 'Quitar' : 'Remove'} ${c}`} className="rounded-full p-0.5 hover:bg-orange-100">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        {importable.length > 0 && (
          <button type="button" onClick={() => addCities(importable)} className="mt-2 text-xs font-bold text-orange-600 hover:underline">
            {es ? `Usar mis áreas de servicio del perfil (${importable.length})` : `Use my profile service areas (${importable.length})`}
          </button>
        )}
        <p className="mt-1.5 text-xs text-slate-500">
          {value.radius_miles
            ? es ? 'Estas ciudades se SUMAN a su radio.' : 'These cities are ADDED to your radius.'
            : es ? 'Con "Todo el estado", las alertas se LIMITAN a estas ciudades. Déjelo vacío para todo el estado.' : 'With Statewide, alerts are LIMITED to these cities. Leave empty for the whole state.'}
        </p>
      </div>
    </div>
  );
};

export default ServiceAreaFields;
